import { useState, useEffect, useCallback } from 'react';
import {
  getDashboardKPIs,
  // getActivationFunnel, // Desactivado temporalmente
  getDrillDownInvoices,
  type KPIResponse,
  // type ActivationFunnel, // Desactivado temporalmente
  type FilterParams,
  type DrillDownFilters,
} from '@/shared/api';

interface DashboardState {
  kpis: KPIResponse | null;
  funnelData: any | null; // ActivationFunnel | null; // Desactivado temporalmente
  loading: {
    kpis: boolean;
    funnel: boolean;
    drillDown: boolean;
  };
  error: string | null;
}

interface DashboardActions {
  refreshDashboard: () => Promise<void>;
  applyFilters: (filters: FilterParams) => Promise<void>;
  handleDrillDown: (filters: DrillDownFilters) => Promise<any>;
  clearError: () => void;
}

export const useDashboard = (): [DashboardState, DashboardActions] => {
  const [state, setState] = useState<DashboardState>({
    kpis: null,
    funnelData: null,
    loading: {
      kpis: false,
      funnel: false,
      drillDown: false,
    },
    error: null,
  });

  const [currentFilters, setCurrentFilters] = useState<FilterParams>({});

  const setLoading = useCallback((key: keyof DashboardState['loading'], value: boolean) => {
    setState(prev => ({
      ...prev,
      loading: {
        ...prev.loading,
        [key]: value,
      },
    }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({
      ...prev,
      error,
    }));
  }, []);

  const loadKPIs = useCallback(async (filters: FilterParams = {}) => {
    setLoading('kpis', true);
    setError(null);
    
    console.log('🔄 Loading KPIs with filters:', filters);
    
    try {
      const result = await getDashboardKPIs(filters);
      console.log('📊 KPIs API result:', result);
      
      if (result.success) {
        console.log('✅ KPIs loaded successfully:', result.data);
        setState(prev => ({
          ...prev,
          kpis: result.data || null,
        }));
      } else {
        console.error('❌ KPIs API error:', result.error);
        setError(result.error || 'Error al cargar métricas KPI');
      }
    } catch (error) {
      console.error('💥 Unexpected error loading KPIs:', error);
      setError('Error inesperado al cargar métricas KPI');
    } finally {
      setLoading('kpis', false);
    }
  }, [setLoading, setError]);

  // Desactivado temporalmente
  /*
  const loadFunnelData = useCallback(async (filters: FilterParams = {}) => {
    setLoading('funnel', true);
    
    try {
      const result = await getActivationFunnel(filters);
      if (result.success) {
        setState(prev => ({
          ...prev,
          funnelData: result.data || null,
        }));
      } else {
        console.warn('Error loading funnel data:', result.error);
      }
    } catch (error) {
      console.error('Error loading funnel data:', error);
    } finally {
      setLoading('funnel', false);
    }
  }, [setLoading]);
  */

  const refreshDashboard = useCallback(async () => {
    await Promise.all([
      loadKPIs(currentFilters),
      // loadFunnelData(currentFilters), // Desactivado temporalmente
    ]);
  }, [loadKPIs, currentFilters]);

  const applyFilters = useCallback(async (filters: FilterParams) => {
    setCurrentFilters(filters);
    await Promise.all([
      loadKPIs(filters),
      // loadFunnelData(filters), // Desactivado temporalmente
    ]);
  }, [loadKPIs]);

  const handleDrillDown = useCallback(async (filters: DrillDownFilters) => {
    setLoading('drillDown', true);
    setError(null);
    
    try {
      const result = await getDrillDownInvoices(filters);
      if (result.success) {
        return result.data;
      } else {
        setError(result.error || 'Error al obtener datos detallados');
        return null;
      }
    } catch (error) {
      setError('Error inesperado al obtener datos detallados');
      console.error('Error in drill down:', error);
      return null;
    } finally {
      setLoading('drillDown', false);
    }
  }, [setLoading, setError]);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  // Initial load - load once when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        loadKPIs({}),
        // loadFunnelData({}), // Desactivado temporalmente
      ]);
    };
    
    loadInitialData();
  }, [loadKPIs]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      refreshDashboard();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [refreshDashboard]);

  const actions: DashboardActions = {
    refreshDashboard,
    applyFilters,
    handleDrillDown,
    clearError,
  };

  return [state, actions];
};
